import { useQuery } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { backendService } from "../services/backend.service";
import { getOtWeekStart } from "../utils/dateUtils";
import { OvertimeBadgeType, type RecurringOvertimeMap } from "../utils/models";

const SINGLE_WEEK = 1;

export function useTemplateNurseWeeklyOT({
	nurseId,
	referenceDate,
	enabled = true,
}: {
	nurseId: number;
	referenceDate: DateTime | undefined;
	enabled?: boolean;
}): boolean {
	const { chosenDepartment } = useHospitalData();
	const { trackError } = useAnalytics();

	const departmentId = chosenDepartment.department_id;
	const weekStartDay = chosenDepartment.week_start_day;
	const otWeekStartIso = enabled && referenceDate ? (getOtWeekStart(referenceDate, weekStartDay).toISODate() ?? "") : "";

	const { data } = useQuery<RecurringOvertimeMap>({
		queryKey: ["recurring-shift-overtime", departmentId, otWeekStartIso, SINGLE_WEEK],
		queryFn: async () => {
			try {
				const response = await backendService.getRecurringShiftOvertime(departmentId, otWeekStartIso, SINGLE_WEEK);
				return response.data;
			} catch (error) {
				trackError("recurring_shift_overtime_fetch_failed", { error_message: (error as Error)?.message });
				throw error;
			}
		},
		enabled: enabled && !!departmentId && !!otWeekStartIso,
		staleTime: 5 * 60 * 1000,
	});

	return useMemo(() => {
		if (!enabled || !data || !otWeekStartIso) return false;
		return data[nurseId]?.[otWeekStartIso]?.type === OvertimeBadgeType.Weekly;
	}, [enabled, data, nurseId, otWeekStartIso]);
}
